import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { TrendingUp, ShoppingCart, Wallet, Boxes, Receipt, BadgeDollarSign, AlertTriangle } from "lucide-react";
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { api } from "../lib/api";
import { useLang } from "../lib/i18n";
import { fmtIDR, todayStr } from "../lib/format";

const COLORS = ["#D97706", "#10B981", "#6366F1", "#F43F5E", "#06B6D4", "#F59E0B", "#8B5CF6", "#64748B"];

export default function Dashboard() {
  const { t } = useLang();
  const { outlet } = useOutletContext() || {};
  const [data, setData] = useState(null);
  const [range, setRange] = useState({ start: todayStr(-30), end: todayStr() });

  useEffect(() => {
    api.get("/reports/dashboard", { params: { start: range.start, end: range.end, outlet_id: outlet || undefined } })
      .then((r) => setData(r.data)).catch(() => {});
  }, [range, outlet]);

  if (!data) return <div className="py-20 text-center text-muted-foreground" data-testid="dashboard-loading">{t("loading")}</div>;

  const kpis = [
    { key: "totalSales", value: data.total_sales, icon: TrendingUp, color: "text-amber-600 bg-amber-50 dark:bg-amber-950/40" },
    { key: "totalProfit", value: data.total_profit, icon: BadgeDollarSign, color: "text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40" },
    { key: "totalPurchase", value: data.total_purchase, icon: ShoppingCart, color: "text-indigo-600 bg-indigo-50 dark:bg-indigo-950/40" },
    { key: "expense", value: data.total_expense, icon: Receipt, color: "text-rose-600 bg-rose-50 dark:bg-rose-950/40" },
    { key: "stockValue", value: data.stock_value, icon: Boxes, color: "text-cyan-600 bg-cyan-50 dark:bg-cyan-950/40" },
    { key: "cashBalance", value: data.cash_balance, icon: Wallet, color: "text-slate-600 bg-slate-100 dark:bg-slate-800" },
  ];

  return (
    <div data-testid="dashboard-page" className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl md:text-3xl font-bold font-heading tracking-tight flex-1">{t("nav_dashboard")}</h1>
        <input data-testid="dashboard-start-date" type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} className="text-sm bg-card border border-border rounded-lg px-3 py-1.5" />
        <input data-testid="dashboard-end-date" type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} className="text-sm bg-card border border-border rounded-lg px-3 py-1.5" />
      </div>

      {data.low_stock?.length > 0 && (
        <div data-testid="low-stock-alert" className="flex items-start gap-3 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-xl px-4 py-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm">
            <span className="font-semibold text-amber-800 dark:text-amber-300">{t("lowStock")}: </span>
            <span className="text-amber-700 dark:text-amber-400">{data.low_stock.map((i) => i.name).join(", ")}</span>
          </div>
        </div>
      )}

      {data.low_stock_by_outlet?.length > 0 && (
        <div data-testid="low-stock-outlet-panel" className="bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900 rounded-xl px-4 py-3">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-rose-600" />
            <span className="font-semibold text-sm text-rose-700 dark:text-rose-400">{t("lowStockPerOutlet")}</span>
          </div>
          <div className="space-y-1">
            {data.low_stock_by_outlet.map((o) => (
              <div key={o.outlet_id} data-testid={`low-stock-outlet-${o.outlet_id}`} className="text-sm text-rose-700 dark:text-rose-300">
                <span className="font-semibold">{o.name}:</span>{" "}
                {o.items.map((i) => `${i.name} (${i.qty}/${i.min_stock})`).join(", ")}
                {!o.is_warehouse && <span className="text-xs text-rose-500"> — {t("transferHint")}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {kpis.map((k) => (
          <div key={k.key} data-testid={`kpi-${k.key}`} className="bg-card border border-border rounded-xl p-4 shadow-xs hover:shadow-md transition-shadow">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-3 ${k.color}`}><k.icon className="w-4 h-4" /></div>
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">{t(k.key)}</div>
            <div className="text-lg xl:text-xl font-bold font-mono tracking-tight truncate">{fmtIDR(k.value)}</div>
          </div>
        ))}
      </div>

      {data.outlet_stock?.length > 0 && (
        <div className="bg-card border border-border rounded-xl p-5 shadow-xs" data-testid="outlet-stock-panel">
          <h3 className="font-heading font-semibold mb-4">{t("stockPerOutlet")}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.outlet_stock.map((o) => (
              <div key={o.outlet_id} data-testid={`outlet-stock-${o.outlet_id}`} className="border border-border rounded-lg px-4 py-3 flex items-center justify-between hover:bg-muted/30 transition-colors">
                <div>
                  <div className="text-sm font-semibold">{o.name}</div>
                  <div className="text-xs text-muted-foreground">{o.items} item</div>
                </div>
                <div className="font-mono font-bold text-cyan-600">{fmtIDR(o.value)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-card border border-border rounded-xl p-5 shadow-xs">
          <h3 className="font-heading font-semibold mb-4">{t("salesTrend")}</h3>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={data.sales_trend}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => fmtIDR(v)} />
              <Legend />
              <Area type="monotone" dataKey="sales" name="Sales" stroke="#D97706" fill="#D97706" fillOpacity={0.15} strokeWidth={2} />
              <Area type="monotone" dataKey="profit" name="Profit" stroke="#10B981" fill="#10B981" fillOpacity={0.15} strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-card border border-border rounded-xl p-5 shadow-xs">
          <h3 className="font-heading font-semibold mb-4">{t("expenseBreakdown")}</h3>
          {data.expense_breakdown?.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={data.expense_breakdown} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={3}>
                  {data.expense_breakdown.map((e, i) => <Cell key={e.name || i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => fmtIDR(v)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : <div className="text-sm text-muted-foreground py-16 text-center">-</div>}
        </div>
      </div>

      <div className="bg-card border border-border rounded-xl p-5 shadow-xs">
        <h3 className="font-heading font-semibold mb-4">{t("topProducts")}</h3>
        {data.top_products?.length ? (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.top_products} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
              <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 12 }} />
              <Tooltip formatter={(v) => fmtIDR(v)} />
              <Bar dataKey="revenue" fill="#D97706" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : <div className="text-sm text-muted-foreground py-10 text-center">-</div>}
      </div>
    </div>
  );
}
